import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppShell } from "../contexts/AppShellContext";

const SHARE_TOKEN_KEY = "cortex_share_access_token";

interface AppGuardProps {
  slug: string;
  children: React.ReactNode;
}

/**
 * Wraps a sub-app route. Checks:
 * 1. User is authenticated
 * 2. User has entitlement for the target app slug
 * If any check fails → redirect to App Selector.
 *
 * Share-link mode: visitor reached the docs surface via /share/<token>
 * — they hold a short-lived JWT in sessionStorage (or, legacy,
 * ?share=<token> in the URL). Either way the server gates the content;
 * UI skips the auth + entitlement checks here.
 */
export default function AppGuard({ slug, children }: AppGuardProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { entitledApps, isLoading: shellLoading } = useAppShell();
  const location = useLocation();
  const hasShareSession = (() => { try { return !!sessionStorage.getItem(SHARE_TOKEN_KEY); } catch { return false; } })();
  const hasShareToken = hasShareSession || !!new URLSearchParams(location.search).get("share");

  if (hasShareToken) {
    // Public-share mode: server gates content; UI just renders.
    return <>{children}</>;
  }

  if (authLoading || shellLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    // Carry the app surface so the login page brands itself (e.g. "Joveo Enable"
    // (Atlas Identity offers each domain whatever methods it resolves to) and returns
    // here after sign-in. On the real host (enable.joveo.ai) detectSurface() also
    // resolves it from the hostname; the ?surface hint makes /enable testable on
    // localhost and any shared host.
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?surface=${slug}&returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  const app = entitledApps.find((a) => a.slug === slug);
  if (!app) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
